import { FormInstance } from 'antd';
import { createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { ResultCode } from '@/infra/adapters/models';
import {
    AuthenticationError,
    DuplicateRecordError,
    ForbiddenError,
    InvalidParamError,
    NotFoundError,
} from '@/infra/exceptions';
import {
    MessageOption,
    MessageType,
    useMessage,
} from '@/v1/main/views/Provider';

interface ErrorContextProps {
    onRequestFail: (err: unknown, form?: FormInstance) => Promise<void>;
}

interface ErrorProviderProps {
    children: React.ReactNode;
}

const ErrorContext = createContext<ErrorContextProps | undefined>(undefined);

export const ErrorProvider = ({ children }: ErrorProviderProps) => {
    const { t } = useTranslation(['interface']);
    const { setMessageOption } = useMessage();

    const onRequestFail = (err: unknown, form?: FormInstance) => {
        if (
            err instanceof InvalidParamError ||
            err instanceof DuplicateRecordError
        ) {
            return Promise.reject(err);
        } else if (err instanceof NotFoundError) {
            const opt: MessageOption = {
                type: MessageType.ERROR,
                content: t('message.action_failed', {
                    ns: 'interface',
                    tip: t('tip.data_not_found', { ns: 'interface' }),
                }),
            };
            setMessageOption(opt);
            return Promise.resolve();
        } else if (err instanceof ForbiddenError) {
            if (err.code == ResultCode.REVIEWING) {
                const opt: MessageOption = {
                    type: MessageType.ERROR,
                    content: t('message.action_failed', {
                        ns: 'interface',
                        tip: t('tip.reviewing', { ns: 'interface' }),
                    }),
                };
                setMessageOption(opt);
                return Promise.resolve();
            } else if (err.code == ResultCode.REVIEWED) {
                const opt: MessageOption = {
                    type: MessageType.ERROR,
                    content: t('message.action_failed', {
                        ns: 'interface',
                        tip: t('tip.reviewed', { ns: 'interface' }),
                    }),
                };
                setMessageOption(opt);
                return Promise.resolve();
            }
            return Promise.reject(err);
        } else if (err instanceof AuthenticationError) {
            if (err.code == ResultCode.USER_INACTIVE) {
                const opt: MessageOption = {
                    type: MessageType.ERROR,
                    content: t('message.account_inactive', { ns: 'interface' }),
                };
                setMessageOption(opt);
                return Promise.resolve();
            } else if (err.code == ResultCode.ACCESS_DENIED) {
                const opt: MessageOption = {
                    type: MessageType.ERROR,
                    content: t('message.access_denied', { ns: 'interface' }),
                };
                setMessageOption(opt);
                return Promise.resolve();
            }
        } else if (err instanceof Error) {
            const opt: MessageOption = {
                type: MessageType.ERROR,
                content: err.message,
            };
            setMessageOption(opt);
            return Promise.resolve();
        }
        return Promise.reject(err);
    };

    return (
        <ErrorContext.Provider value={{ onRequestFail }}>
            {children}
        </ErrorContext.Provider>
    );
};

export const useError = () => {
    const context = useContext(ErrorContext);
    if (context === undefined) {
        throw new Error('useError must be used within a ErrorProvider');
    }
    return context;
};
